import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Loader2, Trash2, ChevronDown, ChevronUp, Search, X, Sparkles } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function JournalPage() {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newEntry, setNewEntry] = useState('');
  const [showNewEntry, setShowNewEntry] = useState(false);
  const [expandedEntry, setExpandedEntry] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [feedbackLoading, setFeedbackLoading] = useState(null);
  const [feedbacks, setFeedbacks] = useState({});

  // Swedish only
  const c = {
    back: 'Tillbaka',
    title: 'Dagbok',
    pageTitle: 'Din dagbok',
    subtitle: 'Ett privat utrymme för dina tankar och känslor.',
    newReflection: 'Ny reflektion',
    placeholder: 'Vad bär du på just nu? Skriv fritt utan att censurera dig själv...',
    cancel: 'Avbryt',
    save: 'Spara',
    emptyText: 'Din dagbok är tom. Börja skriva när du känner dig redo.',
    fetchError: 'Kunde inte hämta dagboken',
    saveSuccess: 'Din reflektion har sparats',
    saveError: 'Kunde inte spara',
    deleteSuccess: 'Inlägg borttaget',
    deleteError: 'Kunde inte ta bort',
    searchPlaceholder: 'Sök i dagboken...',
    noResults: 'Inga inlägg matchar din sökning'
  };

  // Filter entries based on search query
  const filteredEntries = useMemo(() => {
    if (!searchQuery.trim()) return entries;
    const query = searchQuery.toLowerCase();
    return entries.filter(entry => 
      entry.content.toLowerCase().includes(query)
    );
  }, [entries, searchQuery]);

  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = async () => {
    try {
      const response = await axios.get(`${API}/journal`, {
        headers: getAuthHeader()
      });
      setEntries(response.data);
    } catch (error) {
      toast.error(c.fetchError);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!newEntry.trim()) return;
    
    setSaving(true);
    try {
      const response = await axios.post(`${API}/journal`, {
        content: newEntry
      }, {
        headers: getAuthHeader()
      });
      setEntries([response.data, ...entries]);
      setNewEntry('');
      setShowNewEntry(false);
      toast.success(c.saveSuccess);
    } catch (error) {
      toast.error(c.saveError);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/journal/${id}`, {
        headers: getAuthHeader()
      });
      setEntries(entries.filter(e => e.id !== id));
      toast.success(c.deleteSuccess);
    } catch (error) {
      toast.error(c.deleteError);
    }
  };

  const handleGetFeedback = async (entry) => {
    setFeedbackLoading(entry.id);
    try {
      const res = await axios.post(`${API}/ai/diary-feedback`, {
        entry_text: entry.content,
        entry_type: 'journal'
      }, { headers: getAuthHeader() });
      setFeedbacks(prev => ({ ...prev, [entry.id]: res.data.feedback }));
    } catch (e) {
      toast.error('Kunde inte hämta feedback just nu');
    } finally {
      setFeedbackLoading(null);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('sv-SE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-background" data-testid="journal-page">
      {/* Header */}
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-6 py-4 flex justify-between items-center">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/category/thoughts')}
            className="rounded-full hover:bg-secondary/80"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {c.back}
          </Button>
          <h1 className="font-heading text-xl font-light">{c.title}</h1>
          <Button
            variant="ghost"
            onClick={() => setShowSearch(!showSearch)}
            className="rounded-full w-10 h-10 p-0"
            data-testid="search-toggle-btn"
          >
            <Search className="w-5 h-5" />
          </Button>
        </div>
        
        {/* Search bar */}
        {showSearch && (
          <div className="max-w-2xl mx-auto px-6 pb-4 animate-fade-in">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={c.searchPlaceholder}
                className="pl-10 pr-10 rounded-full"
                data-testid="search-input"
                autoFocus
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Main content */}
      <main className="max-w-2xl mx-auto px-6 py-12">
        {/* Intro */}
        <div className="text-center mb-12 space-y-3 animate-fade-in">
          <h2 className="font-heading text-3xl font-light">{c.pageTitle}</h2>
          <p className="text-muted-foreground">
            {c.subtitle}
          </p>
        </div>

        {/* New entry button/form */}
        {!showNewEntry ? (
          <Button
            onClick={() => setShowNewEntry(true)}
            className="w-full rounded-2xl h-16 bg-secondary hover:bg-secondary/80 text-foreground border-2 border-dashed border-border mb-8"
            data-testid="new-entry-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            {c.newReflection}
          </Button>
        ) : (
          <div className="bg-card rounded-2xl p-6 mb-8 shadow-soft animate-fade-in" data-testid="new-entry-form">
            <Textarea
              value={newEntry}
              onChange={(e) => setNewEntry(e.target.value)}
              placeholder={c.placeholder}
              className="min-h-[200px] border-0 bg-transparent focus-visible:ring-0 resize-none text-base leading-relaxed placeholder:text-muted-foreground/50"
              autoFocus
              data-testid="new-entry-textarea"
            />
            <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-border/50">
              <Button
                variant="ghost"
                onClick={() => {
                  setShowNewEntry(false);
                  setNewEntry('');
                }}
                className="rounded-full"
                data-testid="cancel-entry-btn"
              >
                {c.cancel}
              </Button>
              <Button
                onClick={handleSave}
                disabled={!newEntry.trim() || saving}
                className="rounded-full bg-primary hover:bg-primary/90"
                data-testid="save-entry-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : c.save}
              </Button>
            </div>
          </div>
        )}

        {/* Entries list */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary/50" />
          </div>
        ) : filteredEntries.length === 0 ? (
          <div className="text-center py-12 space-y-4">
            {searchQuery ? (
              <>
                <Search className="w-16 h-16 mx-auto text-muted-foreground/30" />
                <p className="text-muted-foreground">{c.noResults}</p>
              </>
            ) : (
              <>
                <div 
                  className="w-32 h-32 mx-auto rounded-full bg-cover bg-center opacity-50"
                  style={{
                    backgroundImage: `url('https://images.unsplash.com/photo-1760442903566-99f3eae250ad?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMG9yZ2FuaWMlMjBzaGFwZXMlMjB3YXJtJTIwY29sb3JzfGVufDB8fHx8MTc2OTg0MTk2N3ww&ixlib=rb-4.1.0&q=85')`
                  }}
                />
                <p className="text-muted-foreground">
                  {c.emptyText}
                </p>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {searchQuery && (
              <p className="text-sm text-muted-foreground mb-4">
                {filteredEntries.length} resultat för "{searchQuery}"
              </p>
            )}
            {filteredEntries.map((entry, index) => (
              <div 
                key={entry.id}
                className="bg-card/50 rounded-2xl p-6 opacity-0 animate-slide-up hover:bg-card transition-colors duration-300"
                style={{ animationDelay: `${index * 0.05}s` }}
                data-testid={`journal-entry-${entry.id}`}
              >
                <div className="flex justify-between items-start mb-3">
                  <span className="text-sm text-muted-foreground">
                    {formatDate(entry.created_at)}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setExpandedEntry(expandedEntry === entry.id ? null : entry.id)}
                      className="rounded-full h-8 w-8 p-0 hover:bg-secondary/80"
                      data-testid={`expand-entry-${entry.id}`}
                    >
                      {expandedEntry === entry.id ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(entry.id)}
                      className="rounded-full h-8 w-8 p-0 hover:bg-destructive/10 hover:text-destructive"
                      data-testid={`delete-entry-${entry.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <p className={`text-foreground leading-relaxed whitespace-pre-wrap ${
                  expandedEntry === entry.id ? '' : 'line-clamp-3'
                }`}>
                  {entry.content}
                </p>
                
                {/* AI Feedback */}
                {expandedEntry === entry.id && (
                  <div className="mt-4 pt-4 border-t border-border/30">
                    {feedbacks[entry.id] ? (
                      <div className="bg-primary/5 rounded-xl p-4 animate-fade-in">
                        <div className="flex items-center gap-2 mb-2">
                          <Sparkles className="w-4 h-4 text-primary" />
                          <span className="text-xs font-medium text-primary">AI-feedback</span>
                        </div>
                        <p className="text-sm text-foreground/80 leading-relaxed">{feedbacks[entry.id]}</p>
                      </div>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleGetFeedback(entry)}
                        disabled={feedbackLoading === entry.id}
                        className="rounded-full text-xs text-muted-foreground hover:text-primary"
                        data-testid={`ai-feedback-btn-${entry.id}`}
                      >
                        {feedbackLoading === entry.id ? (
                          <Loader2 className="w-3 h-3 animate-spin mr-1" />
                        ) : (
                          <Sparkles className="w-3 h-3 mr-1" />
                        )}
                        Få AI-feedback
                      </Button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
